import { useEffect, useState } from "react";

/**
 * Lo que el buscador de la cabecera le pide al listado al que lleva.
 *
 * ## Por qué no basta con leer la dirección al montar
 *
 * Era lo que hacía `ListToolbar`, y fallaba de tres maneras a la vez:
 *
 * 1. **Parpadeo.** El filtro se aplicaba en un efecto, o sea *después* del
 *    primer dibujado: la pantalla enseñaba los 18.645 clientes y un instante
 *    después uno. Con la lista corta no se nota; con la larga es un fogonazo.
 * 2. **Estando ya en la pantalla no pasaba nada.** Buscar un cliente desde
 *    Clientes navega a la misma ruta, y las rutas llevan `key` fija, así que no
 *    se vuelve a montar y el efecto de montaje no corre.
 * 3. **Los documentos nunca se filtraban**, porque su listado no usa la barra
 *    compartida sino un buscador propio.
 *
 * ## Cómo se resuelve
 *
 * El valor **nace** con lo que trae la dirección --sin efecto de por medio, así
 * que el primer dibujado ya sale filtrado-- y además se escucha un aviso que el
 * buscador lanza al navegar, que es lo que cubre quedarse en la misma pantalla.
 *
 * Se usa un aviso del navegador y no el enrutador a propósito: esto lo consumen
 * doce listados y algunos se prueban montados solos, sin enrutador alrededor.
 */
export const AVISO_DE_BUSQUEDA = "busquedaDesdeLaCabecera";

/** Lo que venga en la dirección, o cadena vacía. */
export const deLaDireccion = (parametro) => {
  try {
    return new URLSearchParams(window.location.search).get(parametro) || "";
  } catch (error) {
    return "";
  }
};

/**
 * Anuncia a la pantalla ya montada qué se busca y qué ficha se eligió.
 * Se lanza **después** de navegar: la pantalla nueva lee la dirección al nacer,
 * y este aviso es para la que ya estaba.
 */
export const anunciarBusqueda = (texto, fila) => {
  window.dispatchEvent(
    new CustomEvent(AVISO_DE_BUSQUEDA, { detail: { texto: texto ?? "", fila: fila ?? "" } })
  );
};

/**
 * El texto del buscador de un listado. Sustituye a `useState("")` y devuelve lo
 * mismo, así que la pantalla no se entera de nada más.
 */
export const useBusquedaDeLaCabecera = () => {
  const [texto, setTexto] = useState(() => deLaDireccion("q"));

  useEffect(() => {
    const alBuscar = (evento) => setTexto(evento.detail?.texto ?? "");
    window.addEventListener(AVISO_DE_BUSQUEDA, alBuscar);
    return () => window.removeEventListener(AVISO_DE_BUSQUEDA, alBuscar);
  }, []);

  return [texto, setTexto];
};
